/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/services/api/laborCostService.ts
 * PURPOSE: Labor Cost Analytics API Service - EPIC 10.1 CFO Dashboard
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Sprint Governance - Rule 7: API Contract)
 * SPRINT GOVERNANCE: Rule 7: Performance SLAs (<50ms p95)
 *
 * CTO MANDATE: Real-time labor cost tracking per BU with <25% revenue target
 *
 * @ai-human-orchestration
 * - AIRole: Senior Frontend Developer
 * - HumanHandoff: Local Team validates API integration with NQH CFO
 * - CollaborationLevel: Remote Team implements, CFO validates
 *
 * @business-context
 * - Domain: Vietnamese F&B Labor Cost Analytics
 * - Impact: Revenue Critical - CFO needs real-time cost visibility
 * - Stakeholders: NQH CFO, 5 BU Managers (Thom, BKL, AirDream, LHP, Kupid)
 *
 * @technical-architecture
 * - Layer: Data Access Layer, API Services
 * - Pattern: Service Pattern, Axios Client, Type-Safe API
 * - Dependencies: Axios, TypeScript, Backend Labor Cost APIs
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/
 * - API: docs/03-integrate/01-API-Specifications/
 *  *
 * Sprint 10 EPIC 10.1: Labor Cost Dashboard (8 SP)
 * - Real-time labor cost per BU (5 BUs)
 * - Shift cost breakdown (33 shift codes: M1-M12, A1-A11, N1-N10)
 * - Revenue vs labor cost ratio (<25% target)
 * - Weekly/monthly trend analysis
 * - Cost forecasting (14-30 days)
 */

import { apiClient } from './client';

// #1355: business units come from the API response (summary.bus), never a
// hard-coded list of one tenant's units.
export type BusinessUnitId = string;

// ============= Type Definitions =============

export interface ShiftCostBreakdown {
  shift_code: string;
  shift_type: 'morning' | 'afternoon' | 'night';
  employee_count: number;
  total_hours: number;
  total_cost: number;
  avg_hourly_rate: number;
}

export interface LaborCostMetrics {
  bu_id: string;
  bu_name: string;
  bu_code: string;
  period: {
    start: string;
    end: string;
  };
  metrics: {
    bu_id: string;
    bu_name: string;
    period: {
      start: string;
      end: string;
    };
    labor_cost: number;
    revenue: number;
    labor_cost_ratio: number;
    is_within_target: boolean;
    target_ratio: number;
    employee_count: number;
    hours: {
      total: number;
      regular: number;
      overtime: number;
      night: number;
    };
    cost_per_hour: number;
  };
  shift_breakdown: ShiftCostBreakdown[];
  target: {
    ratio: number;
    status: 'within_target' | 'exceeds_target';
  };
  generated_at: string;
}

export interface LaborCostTrend {
  date: string;
  labor_cost: number;
  revenue: number;
  labor_cost_ratio: number;
  employee_count: number;
}

export interface LaborCostTrendsResponse {
  bu_id: string;
  period: {
    start: string;
    end: string;
    weeks: number;
  };
  trends: LaborCostTrend[];
  analysis: {
    trend_direction: 'increasing' | 'decreasing' | 'stable';
    trend_change: number;
    avg_ratio: number;
    target_ratio: number;
  };
  generated_at: string;
}

export interface LaborCostForecast {
  date: string;
  predicted_cost: number;
  predicted_revenue: number;
  predicted_ratio: number;
  confidence: number;
  alert: boolean;
}

export interface LaborCostForecastResponse {
  bu_id: string;
  forecast_period: {
    start: string;
    end: string;
    days: number;
  };
  forecasts: LaborCostForecast[];
  summary: {
    total_predicted_cost: number;
    total_predicted_revenue: number;
    avg_predicted_ratio: number;
    alert_days: number;
    confidence_avg: number;
  };
  alerts: LaborCostForecast[];
  generated_at: string;
}

export interface LaborCostSummaryResponse {
  period: {
    start: string;
    end: string;
  };
  bus: LaborCostMetrics['metrics'][];
  consolidated: {
    total_labor_cost: number;
    total_revenue: number;
    overall_labor_cost_ratio: number;
    target_ratio: number;
    is_within_target: boolean;
  };
  generated_at: string;
  // DD-0067: "PENDING_REAL_DATA" while the labor-cost feed is not wired
  data_status?: string;
  data_status_message_vi?: string;
}

// ============= Labor Cost API Service =============

export const laborCostService = {
  /**
   * Get labor cost metrics for a specific BU
   * API: GET /api/v1/analytics/labor-cost/bu/{bu_id}
   *
   * @param buId Business unit ID (thom, bkl, airdream, lhp, kupid)
   * @param periodStart Start date (ISO format)
   * @param periodEnd End date (ISO format)
   */
  getByBU: async (
    buId: BusinessUnitId,
    periodStart?: string,
    periodEnd?: string
  ): Promise<LaborCostMetrics> => {
    const params = new URLSearchParams();
    if (periodStart) params.append('period_start', periodStart);
    if (periodEnd) params.append('period_end', periodEnd);

    const response = await apiClient.get<LaborCostMetrics>(
      `/analytics/labor-cost/bu/${buId}?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Get labor cost trends for CFO trend analysis
   * API: GET /api/v1/analytics/labor-cost/trends
   *
   * @param buId Optional BU filter
   * @param weeks Number of weeks (1-12, default: 4)
   */
  getTrends: async (
    buId?: BusinessUnitId,
    weeks: number = 4
  ): Promise<LaborCostTrendsResponse> => {
    const params = new URLSearchParams();
    if (buId) params.append('bu_id', buId);
    params.append('weeks', weeks.toString());

    const response = await apiClient.get<LaborCostTrendsResponse>(
      `/analytics/labor-cost/trends?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Get labor cost forecast based on schedules and historical data
   * API: GET /api/v1/analytics/labor-cost/forecast
   *
   * @param buId Optional BU filter
   * @param forecastDays Days to forecast (14-30)
   */
  getForecast: async (
    buId?: BusinessUnitId,
    forecastDays: number = 14
  ): Promise<LaborCostForecastResponse> => {
    const params = new URLSearchParams();
    if (buId) params.append('bu_id', buId);
    params.append('forecast_days', forecastDays.toString());

    const response = await apiClient.get<LaborCostForecastResponse>(
      `/analytics/labor-cost/forecast?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Get labor cost summary for all 5 BUs
   * API: GET /api/v1/analytics/labor-cost/summary
   *
   * @param periodStart Start date
   * @param periodEnd End date
   */
  getSummary: async (
    periodStart?: string,
    periodEnd?: string
  ): Promise<LaborCostSummaryResponse> => {
    const params = new URLSearchParams();
    if (periodStart) params.append('period_start', periodStart);
    if (periodEnd) params.append('period_end', periodEnd);

    const response = await apiClient.get<LaborCostSummaryResponse>(
      `/analytics/labor-cost/summary?${params.toString()}`
    );
    return response.data;
  },
};

// ============= Utility Functions =============

/**
 * Format VND currency for Vietnamese display
 */
export const formatVND = (amount: number): string => {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
    maximumFractionDigits: 0,
  }).format(amount);
};

/**
 * Format percentage for display
 */
export const formatPercent = (value: number, decimals: number = 1): string => {
  return `${value.toFixed(decimals)}%`;
};

/**
 * Get status color based on labor cost ratio vs target (<25%)
 */
export const getLaborCostStatusColor = (ratio: number, target: number = 25): string => {
  if (ratio <= target * 0.8) return '#22c55e'; // Green - excellent (<20%)
  if (ratio <= target) return '#eab308'; // Yellow - within target (20-25%)
  if (ratio <= target * 1.2) return '#f97316'; // Orange - slightly over (25-30%)
  return '#ef4444'; // Red - critical (>30%)
};

/**
 * Get Vietnamese status text for labor cost
 */
export const getLaborCostStatusText = (ratio: number, target: number = 25): string => {
  if (ratio <= target * 0.8) return 'Xuất sắc';
  if (ratio <= target) return 'Đạt mục tiêu';
  if (ratio <= target * 1.2) return 'Vượt nhẹ';
  return 'Cần cải thiện';
};

export default laborCostService;
